import { useRef } from 'react'
import { useOutsideClick } from '@sequencehq/utils'
import { useAtomValue, useSetAtom } from 'jotai'
import { GreyGrey30, Box } from '@sequencehq/core-components'
import {
  LinkStripeCustomerWidget,
  StripeInspectorItem,
  StripePaymentWidget
} from 'Integrations/index'
import { StripeRetryPaymentWidget } from 'Integrations/integrationsConfig/stripe/InspectorItem/widgets/StripeRetryPaymentWidget'
import {
  clientInvoiceAtom,
  isSavePendingAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { triggeredInspectorItemIdAtom } from '..'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from '@sequencehq/core-components'
import { usePermissions } from 'lib/rbac'
import { usePaymentProviderForInvoice } from 'Invoices/hooks/usePaymentProviderForInvoice'

export const InvoiceStripeInspectorItem = () => {
  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const ref = useRef<HTMLDivElement>(null)
  const setTriggeredInspectorItemId = useSetAtom(triggeredInspectorItemIdAtom)
  const { displayPromiseNotification } = useNotifications()
  const isLineItemSavePending = useAtomValue(isSavePendingAtom)
  const { can } = usePermissions()
  const canManageInvoices = can('org.invoice:update')
  const paymentProvider = usePaymentProviderForInvoice()

  const { mutateAsync: createPaymentSettings, isPending: createIsPending } =
    useMutation(dashboard20240730Client.postInvoiceSettings)

  useOutsideClick({
    ref,
    handler: () => {
      setTriggeredInspectorItemId(null)
    }
  })

  if (!clientInvoice) return null

  const isStripeEnabled: boolean = paymentProvider === 'STRIPE'

  const showRetryPayment =
    isStripeEnabled && // Stripe payments are enabled for this invoice
    clientInvoice.status === 'SENT' && // Invoice is sent
    (clientInvoice.paymentStatus === 'UNPAID' ||
      clientInvoice.paymentStatus === 'PARTIALLY_PAID') // Previous attempt failed

  const otherProviderActive =
    paymentProvider !== 'NONE' && paymentProvider !== 'STRIPE'

  const isDisabled =
    isLineItemSavePending ||
    createIsPending ||
    isStripeEnabled ||
    otherProviderActive ||
    clientInvoice.status === 'VOIDED'

  const stripeControlsDisabled = !canManageInvoices || isDisabled

  const updateInvoiceSettings = async (isStripePaymentSelected: boolean) => {
    await displayPromiseNotification(
      createPaymentSettings({
        invoiceId: clientInvoice.id,
        customerId: clientInvoice.customerId,
        paymentProvider: isStripePaymentSelected ? 'STRIPE' : 'NONE'
      }),
      {
        loading: 'Updating Stripe settings...',
        success: 'Invoice Stripe settings updated',
        error: 'Could not update invoice Stripe settings'
      }
    )
  }

  return (
    <StripeInspectorItem enabled={isStripeEnabled}>
      <div ref={ref}>
        <LinkStripeCustomerWidget customerId={clientInvoice.customerId} />
        <Box
          paddingTop="16px"
          marginTop="16px"
          borderTop={`1px solid ${GreyGrey30}`}
        >
          <StripePaymentWidget
            fieldData={{
              acceptPaymentsViaStripe: {
                disabled: isDisabled,
                value: isStripeEnabled
              }
            }}
            onChange={newData => {
              void updateInvoiceSettings(newData.acceptPaymentsViaStripe.value)
            }}
            disabled={stripeControlsDisabled}
            conflictingProviderActive={otherProviderActive}
            scope="invoice"
          />
        </Box>
        {showRetryPayment && (
          <Box
            paddingTop="16px"
            marginTop="16px"
            borderTop={`1px solid ${GreyGrey30}`}
          >
            <StripeRetryPaymentWidget
              invoiceId={clientInvoice.id}
              disabled={!canManageInvoices}
            />
          </Box>
        )}
      </div>
    </StripeInspectorItem>
  )
}
